// Main-model controls shared by Mac and web Intelligence popovers.
import { withManualAiSharingConsent } from '@/store/aiSharingConsent';
import { useWorkExecution } from './WorkExecutionContext';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { coworkerApi } from '@/lib/api';
import { useCoworkerStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import PageSearchInput from '@/components/Inputs/PageSearchInput';

const effortLabel = value => ({ '': 'Auto', xhigh: 'Extra high', max: 'Maximum' }[value]
  || value.charAt(0).toUpperCase() + value.slice(1));

export default function WorkOrchestratorControls({ taskId, cachedModels, onModelsLoaded, execution: suppliedExecution, consentHost = 'app' }) {
  const inheritedExecution = useWorkExecution();
  const execution = suppliedExecution || inheritedExecution;
  const { colors } = useTheme();
  const task = useCoworkerStore(s => taskId ? s.tasks[taskId] : null);
  const composer = useCoworkerStore(s => s.composerOrchestrator);
  const setComposer = useCoworkerStore(s => s.setComposerOrchestrator);
  const setTask = useCoworkerStore(s => s.setTaskOrchestrator);
  const [models, setModels] = useState(cachedModels || null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [choosing, setChoosing] = useState(false);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [focused, setFocused] = useState('');
  const [retry, setRetry] = useState(0);
  const savingRef = useRef(false);
  const chooseRef = useRef(null), searchRef = useRef(null), listRef = useRef(null);
  const modelRefs = useRef(new Map()), rowLayouts = useRef(new Map());
  const listViewport = useRef(176), listOffset = useRef(0);
  const restoreAfterSave = useRef(null);
  useEffect(() => {
    if (execution) { setModels(execution.models); setError(''); return; }
    if (cachedModels) { setModels(cachedModels); setError(''); return; }
    let current = true;
    setModels(null); setError('');
    coworkerApi.getOrchestratorModels().then(items => {
      if (current) { setModels(items); onModelsLoaded?.(items); }
    })
      .catch(err => { if (current) setError(err.message || 'Could not load models'); });
    return () => { current = false; };
  }, [retry, cachedModels, onModelsLoaded, execution?.models]);
  const selected = execution ? execution.settings : taskId ? task : composer;
  const slug = selected?.orchestratorModel || '';
  const effort = selected?.reasoningEffort || '';
  const actualSlug = slug || (execution ? execution.defaultModel : task?.model);
  const model = models?.find(item => item.slug === actualSlug);
  const levels = model?.reasoningLevels || [];
  const disabled = execution ? (saving || execution.disabled) : saving || (taskId && (!task || task.status === 'queued'));
  const windows = Platform.OS === 'windows';
  useEffect(() => {
    if (!windows || !choosing) return undefined;
    const frame = requestAnimationFrame(() => searchRef.current?.focus?.());
    return () => cancelAnimationFrame(frame);
  }, [windows, choosing]);
  useEffect(() => {
    if (!windows || disabled || !restoreAfterSave.current) return undefined;
    // Wait for the owner to re-enable the controls before restoring focus.
    const frame = requestAnimationFrame(() => {
      const target = restoreAfterSave.current; restoreAfterSave.current = null;
      (target?.kind === 'model' ? modelRefs.current.get(target.slug) || searchRef.current : chooseRef.current)?.focus?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [windows, disabled, choosing]);
  const focusProps = key => windows ? { enableFocusRing: false, onFocus: () => setFocused(key),
    onBlur: () => setFocused(current => current === key ? '' : current) } : {};
  const focusStyle = key => windows && execution?.keyboardFocusVisible !== false && focused === key
    ? { borderWidth: 1, borderColor: colors.text_color_tertiary } : undefined;
  const save = async (orchestratorModel, reasoningEffort) => {
    if (disabled || savingRef.current) return;
    savingRef.current = true; setSaving(true); setError(''); setNotice('');
    try {
      const settings = { orchestratorModel, reasoningEffort };
      if (execution) await execution.update(settings);
      else if (taskId) {
        const out = await setTask(taskId, settings);
        setNotice(out.applied === 'next_turn' ? 'Applies on your next turn.' : 'Saved for your next message.');
      } else setComposer(settings);
      if (windows) restoreAfterSave.current = { kind: 'choose' };
      setChoosing(false); setQuery('');
    } catch (err) {
      if (windows && choosing) restoreAfterSave.current = { kind: 'model', slug: orchestratorModel };
      setError(err.message || 'Could not save main model');
    }
    finally { savingRef.current = false; setSaving(false); }
  };
  const row = { minHeight: 38, padding: 8, borderRadius: 8, flexDirection: 'row', alignItems: 'center', gap: 8 };
  function closeChoices() {
    setChoosing(false);
    chooseRef.current?.focus?.();
  }
  function focusModel(slug) {
    const bounds = rowLayouts.current.get(slug);
    if (bounds) {
      const top = listOffset.current, bottom = top + listViewport.current;
      if (bounds.y < top || bounds.y + bounds.height > bottom) {
        listRef.current?.scrollTo?.({ y: bounds.y < top ? bounds.y : Math.max(0, bounds.y + bounds.height - listViewport.current), animated: false });
      }
    }
    modelRefs.current.get(slug)?.focus?.();
  }
  function modelKey(event, slug) {
    if (event.nativeEvent.ctrlKey || event.nativeEvent.altKey || event.nativeEvent.metaKey || event.nativeEvent.shiftKey) return;
    const code = event.nativeEvent.code || event.nativeEvent.key;
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', 'NumpadEnter', 'Escape'].includes(code)) return;
    event.stopPropagation?.();
    if (code === 'Escape') { closeChoices(); return; }
    if (disabled || !choices.length) return;
    if (code === 'Enter' || code === 'NumpadEnter') {
      const item = choices.find(item => item.slug === slug);
      if (item) chooseModel(item);
      return;
    }
    // Home/End retain ordinary text-editing behavior in the search input.
    if (slug == null && (code === 'Home' || code === 'End')) return;
    const index = choices.findIndex(item => item.slug === slug);
    const next = code === 'Home' ? 0 : code === 'End' ? choices.length - 1
      : code === 'ArrowUp' ? (index < 0 ? choices.length - 1 : (index - 1 + choices.length) % choices.length)
        : (index + 1) % choices.length;
    focusModel(choices[next].slug);
  }
  const chooseModel = item => item.slug ? withManualAiSharingConsent(() => save(item.slug, ''), { host: consentHost }) : save('', '');
  const modelRow = (item) => <Pressable key={item.slug} disabled={disabled}
    accessibilityRole="button" accessibilityLabel={`Main model: ${item.name}`}
    accessibilityState={{ selected: slug === item.slug, disabled: !!disabled }}
    {...focusProps(`model:${item.slug}`)}
    {...(windows && { ref: node => { if (node) modelRefs.current.set(item.slug, node); else modelRefs.current.delete(item.slug); },
      onLayout: event => rowLayouts.current.set(item.slug, event.nativeEvent.layout),
      keyDownEvents: ['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', 'NumpadEnter', 'Escape'].map(code => ({ code, handled: true })),
      onKeyDown: event => modelKey(event, item.slug) })}
    {...(Platform.OS === 'web' ? { 'aria-pressed': slug === item.slug } : {})}
    onPress={() => chooseModel(item)}
    style={[row, windows && { borderWidth: 1, borderColor: 'transparent' }, slug === item.slug && { backgroundColor: colors.solid_color_tertiary }, focusStyle(`model:${item.slug}`)]}>
    <Text size="s" style={{ flex: 1 }} numberOfLines={2}>{item.name}</Text>
    {item.credits > 0 && <Text size="xxs" color="tertiary">from {item.credits} cr</Text>}
    {slug === item.slug && <Icon name="check" size={12} color={colors.text_color_primary} />}
  </Pressable>;
  // Also exclude Arya from older cached catalogs; Gab Work Agent is arya-agent.
  const filtered = models?.filter(item => item.slug !== 'arya'
    && `${item.name} ${item.slug}`.toLowerCase().includes(query.trim().toLowerCase())) || [];
  const choices = [...(!query.trim() && execution?.capabilities?.autoModel !== false ? [{ slug: '', name: 'Auto' }] : []), ...filtered];
  return <View style={{ paddingBottom: 14, marginBottom: 14, borderBottomWidth: 1, borderBottomColor: colors.border_color_secondary }}>
    <Text size="s" weight="semibold">Main model</Text>
    <Text size="xs" color="secondary" style={{ marginTop: 3, marginBottom: 8 }}>Plans and coordinates this Work task.</Text>
    <Pressable accessibilityRole="button" accessibilityLabel="Choose main Work model"
      ref={chooseRef}
      accessibilityState={{ expanded: choosing, disabled: !!disabled }} disabled={disabled}
      {...focusProps('choose')}
      {...(windows && { keyDownEvents: ['ArrowDown', 'ArrowUp'].map(code => ({ code, handled: true })),
        onKeyDown: event => { if (['ArrowDown', 'ArrowUp'].includes(event.nativeEvent.code) && !disabled) {
          event.stopPropagation?.(); if (choosing) focusModel(choices[event.nativeEvent.code === 'ArrowUp' ? choices.length - 1 : 0]?.slug);
          else setChoosing(true);
        } } })}
      {...(Platform.OS === 'web' ? { 'aria-expanded': choosing } : {})}
      onPress={() => setChoosing(!choosing)} style={[row, { borderWidth: 1, borderColor: colors.border_color_secondary }, focusStyle('choose')]}>
      <Icon name="brain" size={14} color={colors.text_color_secondary} />
      <Text size="s" numberOfLines={2} style={{ flex: 1 }}>{slug ? (model?.name || slug) : 'Auto'}</Text>
      <Icon name={choosing ? 'chevron-up' : 'chevron-down'} size={10} color={colors.text_color_secondary} />
    </Pressable>
    {choosing && <View style={{ marginTop: 8 }}>
      <PageSearchInput value={query} onChangeText={setQuery} placeholder="Search models" noPadding catalogVariant
        {...(windows && { inputRef: searchRef, keyDownEvents: ['ArrowDown', 'ArrowUp', 'Escape'].map(code => ({ code, handled: true })), onKeyDown: event => modelKey(event, null) })} />
      <ScrollView ref={listRef} style={{ maxHeight: 176, marginTop: 4 }} keyboardShouldPersistTaps="handled"
        {...(windows && { onLayout: event => { listViewport.current = event.nativeEvent.layout.height; },
          onScroll: event => { listOffset.current = event.nativeEvent.contentOffset.y; }, scrollEventThrottle: 100 })}>
        {choices.map(modelRow)}
        {models && !filtered.length && <Text size="xs" color="secondary" style={{ padding: 8 }}>No matching models</Text>}
      </ScrollView>
    </View>}
    {!models && !error && <Text size="xs" color="secondary" style={{ marginTop: 8 }}>Loading models…</Text>}
    {!choosing && model && <>
      {levels.length > 0 ? <>
        <Text size="xs" color="secondary" style={{ marginTop: 12, marginBottom: 6 }}>Main model thinking</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
          {['', ...levels].map(value => <Pressable key={value} disabled={disabled}
            accessibilityRole="button" accessibilityLabel={`Main model thinking: ${effortLabel(value)}`}
            accessibilityState={{ selected: effort === value, disabled: !!disabled }}
            {...focusProps(`effort:${value}`)}
            {...(Platform.OS === 'web' ? { 'aria-pressed': effort === value } : {})}
            onPress={() => save(slug || actualSlug, value)}
            style={[{ paddingHorizontal: 10, paddingVertical: 7, borderRadius: 7, borderWidth: 1,
              borderColor: effort === value ? colors.text_color_tertiary : colors.border_color_secondary,
              backgroundColor: effort === value ? colors.solid_color_tertiary : 'transparent' }, focusStyle(`effort:${value}`)]}>
            <Text size="xs">{effortLabel(value)}</Text>
          </Pressable>)}
        </View>
      </> : <Text size="xs" color="secondary" style={{ marginTop: 8 }}>This model manages its own thinking.</Text>}
    </>}
    {saving && <Text size="xs" color="secondary" style={{ marginTop: 8 }}>Saving…</Text>}
    {!!notice && <Text size="xs" color="secondary" accessibilityLiveRegion="polite" style={{ marginTop: 8 }}>{notice}</Text>}
    {!!error && <View style={{ marginTop: 8 }}>
      <Text size="xs" accessibilityRole="alert" style={{ color: colors.text_color_danger || '#dc2626' }}>{error}</Text>
      {!models && <Pressable onPress={() => setRetry(retry + 1)} accessibilityRole="button" style={row}><Text size="xs">Retry loading models</Text></Pressable>}
    </View>}
  </View>;
}
